import { useEffect, useRef, useState } from "react";

const TOKEN = process.env.REACT_APP_FRESHDESK_TOKEN;
const HOST = process.env.REACT_APP_FRESHDESK_HOST;
const WIDGET_ID = process.env.REACT_APP_FRESHDESK_WIDGET_ID;
const WIDGET_SRC = process.env.REACT_APP_FRESHDESK_WIDGET_SRC;

const FreshDeskWidget = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  // Store the latest auth data received via postMessage
  const authDataRef = useRef({
    name: "",
    email: "",
    client: "STAGING1",
    application: "DigiClass",
  });

  // ─── Step 1: Load the Freshdesk script and init the widget ───────────────
  // Runs once on mount. Initialises without contact properties so the widget
  // is usable even before the user has logged in (login-page scenario).
  useEffect(() => {
    if (isLoaded) return;

    const initFreshDesk = () => {
      if (!window.fdWidget) return;

      // Extract contact info from query params (Handy for mobile)
      const urlParams = new URLSearchParams(window.location.search);
      const queryName = urlParams.get("name") || "";
      const queryEmail = urlParams.get("email") || "";
      const queryClient = (urlParams.get("client") || "").toUpperCase();
      const queryApplication = urlParams.get("application") || "";

      // Update ref so that if we re-initialize later, we keep this data
      if (queryName || queryEmail || queryClient || queryApplication) {
        authDataRef.current = {
          name: queryName || authDataRef.current.name,
          email: queryEmail || authDataRef.current.email,
          client: queryClient || authDataRef.current.client,
          application: queryApplication || authDataRef.current.application,
        };
      }

      const { name, email, client, application } = authDataRef.current;
      const hasAuthData = name.trim() !== "" || email.trim() !== "";

      window.fdWidget.init({
        token: TOKEN,
        host: HOST,
        widgetId: WIDGET_ID,
        ...(hasAuthData && {
          contactProperties: {
            name: name,
            email: email,
          },
        }),
        ticketProperties: {
          cf_application: application,
          cf_client: client.toUpperCase(),
        },
      });

      window.fdWidget.on("widget:loaded", () => {
        setIsLoaded(true);
        const isMobile =
          urlParams.get("deviceType")?.toLowerCase() === "mobile";
        if (isMobile) {
          // Small delay before opening helps prevent "broken alignment" issues on mobile
          setTimeout(() => {
            window.fdWidget.open();
          }, 500);
        }
      });

      window.fdWidget.on("widget:opened", () => {
        window.parent.postMessage(
          { type: "FRESHDESK_WIDGET_STATE", isOpen: true },
          "*",
        );
      });

      window.fdWidget.on("widget:closed", () => {
        window.parent.postMessage(
          { type: "FRESHDESK_WIDGET_STATE", isOpen: false },
          "*",
        );
      });
    };

    const initialize = (doc, id) => {
      if (doc.getElementById(id)) {
        initFreshDesk();
      } else {
        const script = doc.createElement("script");
        script.id = id;
        script.async = true;
        script.src = WIDGET_SRC;
        script.onload = initFreshDesk;
        script.onerror = () => {
          setError("Failed to load Help Desk. Please check your connection.");
        };
        doc.head.appendChild(script);
      }
    };

    // Timeout to show error if widget:loaded takes too long (e.g. 15 seconds)
    const timeoutId = setTimeout(() => {
      if (!isLoaded && !error) {
        setError(
          "Loading is taking longer than expected. Please wait or refresh.",
        );
      }
    }, 15000);

    initialize(document, "Freshdesk-js-sdk");

    return () => clearTimeout(timeoutId);
  }, [isLoaded, error]);

  // ─── Step 2: Listen for auth data from the parent application ────────────
  // The parent app posts a message of the shape:
  //   { type: 'FRESHDESK_AUTH', name: 'John Doe', email: 'john@example.com' }
  //
  // When the message arrives the widget is destroyed and then re-initialised
  // with the supplied contact properties (post-login scenario).
  useEffect(() => {
    const handleMessage = (event) => {
      const { type, name, email, client, application } = event.data || {};

      if (type !== "FRESHDESK_AUTH") return;

      // Keep the latest auth info in a ref so the destroy callback can
      // safely close over it without needing authData in the dep array.
      authDataRef.current = {
        name: name || "",
        email: email || "",
        client: (client || authDataRef.current.client).toUpperCase(),
        application: application || authDataRef.current.application,
      };

      if (!isLoaded || !window.fdWidget) return;

      const reinitWidget = () => {
        window.fdWidget.on("widget:destroyed", () => {
          const {
            name: contactName,
            email: contactEmail,
            client: contactClient,
            application: contactApplication,
          } = authDataRef.current;

          const newHasAuthData =
            contactName.trim() !== "" || contactEmail.trim() !== "";

          window.fdWidget.init({
            token: TOKEN,
            host: HOST,
            widgetId: WIDGET_ID,
            ...(newHasAuthData && {
              contactProperties: {
                name: contactName,
                email: contactEmail,
              },
            }),
            ticketProperties: {
              cf_application: contactApplication,
              cf_client: contactClient.toUpperCase(),
            },
          });

          window.fdWidget.on("widget:loaded", () => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get("deviceType")?.toLowerCase() === "mobile") {
              // Small delay before opening helps prevent "broken alignment" issues on mobile
              setTimeout(() => {
                window.fdWidget.open();
              }, 500);
            }
          });

          window.fdWidget.on("widget:opened", () => {
            window.parent.postMessage(
              { type: "FRESHDESK_WIDGET_STATE", isOpen: true },
              "*",
            );
          });

          window.fdWidget.on("widget:closed", () => {
            window.parent.postMessage(
              { type: "FRESHDESK_WIDGET_STATE", isOpen: false },
              "*",
            );
          });
        });
        window.fdWidget.destroy();
      };

      if (
        window.fdWidget.user &&
        typeof window.fdWidget.user.clear === "function"
      ) {
        try {
          const clearResult = window.fdWidget.user.clear();
          if (clearResult && typeof clearResult.then === "function") {
            clearResult.then(reinitWidget).catch((err) => {
              //   console.error("Error in user.clear promise:", err);
              reinitWidget();
            });
          } else {
            reinitWidget();
          }
        } catch (e) {
          //   console.error("Error calling user.clear:", e);
          reinitWidget();
        }
      } else {
        reinitWidget();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isLoaded]);

  if (isLoaded) return null;

  return (
    <div className="loader-container">
      {!error && <div className="loader"></div>}
      <div
        className="loader-text"
        style={
          error
            ? { color: "#d32f2f", textAlign: "center", padding: "0 20px" }
            : {}
        }
      >
        {error || "Loading Help Desk..."}
      </div>
      {error && (
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "20px",
            padding: "8px 20px",
            backgroundColor: "#448ee0",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      )}
    </div>
  );
};

export default FreshDeskWidget;
